/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { toast } from "sonner";

import type { PortalOrganisation } from "@/types/portal";
import { useAuth } from "@/contexts/AuthContext";
import { portalFetch } from "@/lib/portalApi";
import {
  keepSelectedOrganisationIdForThisTab,
  replaceSelectedOrganisationDefaultIfUnusable,
  readSelectedOrganisationId,
  writeSelectedOrganisationId,
} from "@/lib/selectedOrganisationStorage";
import type { Subscription } from "@/services/api/platform";
import { platformApi } from "@/services/api/platform";

/**
 * Feature access flags computed from user role on the server.
 * These are passed down to client components via context.
 */
export type FeatureAccess = {
  canRefreshConnectors: boolean;
  canEditConnectorProperties: boolean;
  canViewConnectors: boolean;
  canManageUsers: boolean;
  canAccessAdminDashboard: boolean;
  canDeleteResources: boolean;
};

const DEFAULT_FEATURE_ACCESS: FeatureAccess = {
  canRefreshConnectors: false,
  canEditConnectorProperties: false,
  canViewConnectors: true,
  canManageUsers: false,
  canAccessAdminDashboard: false,
  canDeleteResources: false,
};

type PortalDataContextValue = {
  loading: boolean;
  error: string | null;
  organisations: PortalOrganisation[];
  selectedOrganisation?: PortalOrganisation;
  setSelectedOrganisationId: (organisationId: string) => void;
  refreshOrganisations: () => Promise<void>;
  featureAccess: FeatureAccess;
  /** Active service names for the selected org. null = still loading or super admin bypass. */
  activeServices: string[] | null;
  /**
   * True when the subscriptions fetch FAILED, so `activeServices` is `[]` as a
   * deny-all default rather than a real answer. Without this the two are
   * indistinguishable, and a caller that denies on an empty list cannot tell
   * "subscribed to nothing" from "one endpoint blipped".
   *
   * Gates that HIDE something (nav, the widget picker) should keep denying —
   * fail-closed is right there. Gates that would REMOVE something already on
   * screen must not: see SpaceWidgetCell.
   */
  activeServicesFailed: boolean;
  /** Same, for the my-permissions fetch: `permissions` is `{}` (deny all)
   * because the call failed, not because the user was granted nothing. */
  permissionsFailed: boolean;
  /** Full subscription objects for the selected org. null = still loading or super admin bypass. */
  activeSubscriptions: Subscription[] | null;
  /** Backend-driven feature permissions. null = still loading, {} = deny all. */
  permissions: Record<string, Record<string, boolean>> | null;
  /** Whether user bypasses group checks (org_owner, org_admin, platform_admin). */
  bypassesGroups: boolean;
  /** Larasoft platform staff. Distinct from bypassesGroups (also true for
   *  org_owner/admin); used to exempt platform admins from org-level guardrails
   *  such as the org-admin daily schedule-frequency cap. */
  isPlatformAdmin: boolean;
};

const PortalDataContext = createContext<PortalDataContextValue | undefined>(
  undefined,
);

type UserOrganisation = {
  id: string;
  name: string;
  slug: string;
  role: string;
  can_create_servers?: boolean;
  can_manage_users?: boolean;
  can_manage_billing?: boolean;
  billing_currency?: string;
};

type UserOrganisationsResponse = {
  items: UserOrganisation[];
};

type AllOrganisation = {
  id: string;
  name: string;
  slug: string;
  status?: string;
  billing_currency?: string;
};

type AllOrganisationsResponse = {
  items: AllOrganisation[];
};

/**
 * Fetch user's organisations (with their role in each).
 * Used for regular users who only see orgs they're members of.
 */
async function fetchUserOrganisations(
  token: string,
): Promise<PortalOrganisation[]> {
  const data = await portalFetch<UserOrganisationsResponse>(
    "/api/v1/users/me/organisations",
    { token },
  );
  return data.items.map((org) => ({
    organisationId: org.id,
    organisationName: org.name,
    slug: org.slug,
    role: org.role as PortalOrganisation["role"],
    billingCurrency: org.billing_currency,
    data: {
      Id: org.id,
      CompanyName: org.name,
    },
  }));
}

/**
 * Fetch ALL organisations (platform admin view).
 * Admins get org_owner role for all organisations.
 * Fetches in pages since API limits to 100 per request.
 */
async function fetchAllOrganisations(
  token: string,
): Promise<PortalOrganisation[]> {
  const allOrgs: PortalOrganisation[] = [];
  const pageSize = 100;
  let offset = 0;
  let hasMore = true;

  while (hasMore) {
    const params = new URLSearchParams({
      offset: String(offset),
      limit: String(pageSize),
    });
    const data = await portalFetch<AllOrganisationsResponse>(
      `/api/v1/organisations/?${params}`,
      { token },
    );

    const mapped = data.items.map((org) => ({
      organisationId: org.id,
      organisationName: org.name,
      slug: org.slug,
      role: "org_owner" as PortalOrganisation["role"],
      status: org.status,
      billingCurrency: org.billing_currency,
      data: {
        Id: org.id,
        CompanyName: org.name,
      },
    }));

    allOrgs.push(...mapped);
    offset += pageSize;
    hasMore = data.items.length === pageSize;
  }

  return allOrgs;
}

type PortalDataProviderProps = {
  children: ReactNode;
  featureAccess?: FeatureAccess;
};

export function PortalDataProvider({
  children,
  featureAccess = DEFAULT_FEATURE_ACCESS,
}: PortalDataProviderProps) {
  const { token, session } = useAuth();
  const isAdmin = session?.user?.is_admin === true;
  const [organisations, setOrganisations] = useState<PortalOrganisation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedOrganisationId, setSelectedOrganisationId] = useState<
    string | null
  >(null);
  const [activeServices, setActiveServices] = useState<string[] | null>(null);
  const [activeServicesFailed, setActiveServicesFailed] = useState(false);
  const [permissionsFailed, setPermissionsFailed] = useState(false);
  const [activeSubscriptions, setActiveSubscriptions] = useState<Subscription[] | null>(null);
  const [permissions, setPermissions] = useState<Record<string, Record<string, boolean>> | null>(null);
  const [bypassesGroups, setBypassesGroups] = useState<boolean>(false);
  const [isPlatformAdmin, setIsPlatformAdmin] = useState<boolean>(false);

  const syncSelectedOrganisation = useCallback(
    (orgs: PortalOrganisation[]): string | null => {
      // This tab's own choice first: a token adopted from another tab
      // re-runs this, and must not move this tab to that tab's org.
      const stored = readSelectedOrganisationId();
      const matched = orgs.find((org) => org.organisationId === stored);
      const fallback = orgs[0]?.organisationId ?? null;
      const next = matched?.organisationId ?? fallback;
      setSelectedOrganisationId(next);
      // This tab only: a re-sync is not the user picking an org, so it must
      // not change the org new tabs start on, unless that default is missing
      // or no longer usable.
      if (next) {
        keepSelectedOrganisationIdForThisTab(next);
        replaceSelectedOrganisationDefaultIfUnusable(
          orgs.map((org) => org.organisationId),
          next,
        );
      }
      return next;
    },
    [],
  );

  const load = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // Platform admins see all organisations, regular users see only their own
      const orgs = isAdmin
        ? await fetchAllOrganisations(token)
        : await fetchUserOrganisations(token);
      setOrganisations(orgs);
      syncSelectedOrganisation(orgs);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Unable to load organisations";
      setError(errorMessage);
      toast.error(errorMessage);
      setOrganisations([]);
      setSelectedOrganisationId(null);
    } finally {
      setLoading(false);
    }
  }, [token, isAdmin, syncSelectedOrganisation]);

  useEffect(() => {
    void load();
  }, [load]);

  // Fetch active subscriptions when selected org changes
  useEffect(() => {
    if (!token || !selectedOrganisationId) {
      setActiveServices(null);
      setActiveSubscriptions(null);
      return;
    }

    // Super admins bypass subscription filtering
    if (isAdmin) {
      setActiveServices(null);
      setActiveSubscriptions(null);
      return;
    }

    let cancelled = false;

    async function loadSubscriptions() {
      try {
        const subs: Subscription[] = await platformApi.getOrgServiceSubscriptions(
          token!,
          selectedOrganisationId!,
        );
        if (!cancelled) {
          const activeSubs = subs.filter((s) => s.status.toLowerCase() === "active");
          const serviceNames = activeSubs.map((s) => s.service_name);
          // If org has any connector-category subscription, include "connectors"
          // so the Connectors nav item is visible
          if (activeSubs.some((s) => s.category === "connector") && !serviceNames.includes("connectors")) {
            serviceNames.push("connectors");
          }
          setActiveServices(serviceNames);
          setActiveSubscriptions(activeSubs);
          setActiveServicesFailed(false);
        }
      } catch {
        // On error, set empty array (no services visible) rather than null (bypass).
        // The flag records that this [] is a FAILURE default, not an answer.
        if (!cancelled) {
          setActiveServices([]);
          setActiveSubscriptions([]);
          setActiveServicesFailed(true);
        }
      }
    }

    void loadSubscriptions();

    return () => {
      cancelled = true;
    };
  }, [token, selectedOrganisationId, isAdmin]);

  // Fetch feature permissions from backend RBAC when selected org changes
  useEffect(() => {
    if (!token || !selectedOrganisationId) {
      setPermissions(null);
      setBypassesGroups(false);
      setIsPlatformAdmin(false);
      return;
    }

    let cancelled = false;

    async function loadPermissions() {
      try {
        const data = await platformApi.getMyPermissions(token!);
        if (!cancelled) {
          setPermissions(data.permissions);
          setBypassesGroups(data.bypasses_groups ?? false);
          setIsPlatformAdmin(data.is_platform_admin ?? false);
          setPermissionsFailed(false);
        }
      } catch {
        // On error, set empty object (deny all) rather than null (loading).
        // The flag records that this {} is a FAILURE default, not an answer.
        if (!cancelled) {
          setPermissions({});
          setBypassesGroups(false);
          setIsPlatformAdmin(false);
          setPermissionsFailed(true);
        }
      }
    }

    void loadPermissions();

    return () => {
      cancelled = true;
    };
  }, [token, selectedOrganisationId]);

  const value = useMemo<PortalDataContextValue>(
    () => ({
      loading,
      error,
      organisations,
      selectedOrganisation: organisations.find(
        (org) => org.organisationId === selectedOrganisationId,
      ),
      setSelectedOrganisationId: (organisationId: string) => {
        setSelectedOrganisationId(organisationId);
        writeSelectedOrganisationId(organisationId);
      },
      refreshOrganisations: load,
      featureAccess,
      activeServices,
      activeServicesFailed,
      permissionsFailed,
      activeSubscriptions,
      permissions,
      bypassesGroups,
      isPlatformAdmin,
    }),
    [
      error,
      loading,
      load,
      organisations,
      selectedOrganisationId,
      featureAccess,
      activeServices,
      activeServicesFailed,
      permissionsFailed,
      activeSubscriptions,
      permissions,
      bypassesGroups,
      isPlatformAdmin,
    ],
  );

  return (
    <PortalDataContext.Provider value={value}>
      {children}
    </PortalDataContext.Provider>
  );
}

export function usePortalData() {
  const context = useContext(PortalDataContext);
  if (!context) {
    throw new Error("usePortalData must be used within a PortalDataProvider");
  }
  return context;
}
